import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { allElements } from "constants/constants";
import {
    initialOeeSummaryDashboardFilters,
    OeeSummaryDashboardContext,
    validOeeSummaryDefaultFields,
} from "contexts/OeeSummaryDashboardContext/OeeSummaryDashboardContext";
import { IOeeSummaryDashboardFilters } from "contexts/OeeSummaryDashboardContext/OeeSummaryDashboardContextInterfaces";
import { objectComparator } from "utilities/utilities";
import useOeeSummaryFilterOptions from "../../hooks/useOeeSummaryFilterOptions";
import { IUseOeeSummaryDashboardFiltersReturn } from "./OeeSummaryDashboardFiltersInterfaces";

/**
 * "All" is exclusive in the machine center multi-select, and a selection of length <= 1 falls back to All,
 * the same rule as the Summary Report filters.
 */
const normaliseMachineCenterSelection = (selection: (number | string)[]): (number | string)[] => {
    if (selection.length <= 1) return [allElements];
    if (selection.length > 1 && selection[0] === allElements) return selection.filter((id) => id !== allElements);
    if (selection.includes(allElements)) return [allElements];
    return selection;
};

const useOeeSummaryDashboardFilters = (): IUseOeeSummaryDashboardFiltersReturn => {
    const {
        filters: contextFilters,
        handleFiltersChange: handleContextFiltersChange,
        handleRemoveDefaultFilters,
        handleSelectedProductionLineChange,
        handleSetFiltersDefault,
        handleShowAllProductionLinesChange,
        loadingDefaultOperations,
        showAllProductionLines,
        userFiltersDefault,
    } = useContext(OeeSummaryDashboardContext);
    const [filters, setFilters] = useState<IOeeSummaryDashboardFilters>(contextFilters);
    const { loading, machineCentersOptions, shiftsOptions } = useOeeSummaryFilterOptions();

    useEffect(() => {
        setFilters(contextFilters);
    }, [contextFilters]);

    const handleFiltersChange = useCallback((newFilters: Partial<IOeeSummaryDashboardFilters>) => {
        setFilters((previousFilters) => ({
            ...previousFilters,
            ...newFilters,
            ...(newFilters.machineCenter && {
                machineCenter: normaliseMachineCenterSelection(newFilters.machineCenter),
            }),
        }));
    }, []);

    const applyFilters = () => {
        handleContextFiltersChange({ ...filters, productionLine: contextFilters.productionLine });
    };

    const resetFilters = () => {
        setFilters(initialOeeSummaryDashboardFilters);
        handleContextFiltersChange(initialOeeSummaryDashboardFilters);
        if (!showAllProductionLines) {
            handleShowAllProductionLinesChange(true);
            handleSelectedProductionLineChange(allElements, "");
        }
    };

    const setDefaultFilters = async () => {
        try {
            await handleSetFiltersDefault(filters);
            applyFilters();
        } catch (error) {
            console.error(error);
        }
    };

    const removeDefaultFilters = async () => {
        try {
            await handleRemoveDefaultFilters();
            resetFilters();
        } catch (error) {
            console.error(error);
        }
    };

    const isFiltersDefault = useMemo(
        () =>
            userFiltersDefault ? objectComparator(userFiltersDefault, filters, validOeeSummaryDefaultFields) : false,
        [userFiltersDefault, filters],
    );

    return {
        applyFilters,
        filters,
        handleFiltersChange,
        isFiltersDefault,
        isLoading: loading,
        loadingDefaultOperations,
        machineCentersOptions,
        removeDefaultFilters,
        resetFilters,
        setDefaultFilters,
        shiftsOptions,
        userFiltersDefault,
    };
};

export default useOeeSummaryDashboardFilters;
